import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X, Trash2, ChevronDown } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Tag } from "@/components/site/Bits";
import { STATUS, TYPE_LABEL } from "@/pages/Apply";

function AppCard({ a, onChange }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(a.note || "");
  const set = async (status) => {
    try { await api.patch(`/admin/applications/${a.id}`, { status, note }); toast.success(status === "accepted" ? "انقبل التقديم" : status === "rejected" ? "انرفض التقديم" : "رجع قيد المراجعة"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => { if (!window.confirm("تحذف التقديم نهائياً؟")) return; await api.delete(`/admin/applications/${a.id}`); onChange(); };
  return (
    <div className="glass rounded-2xl" data-testid={`admin-app-${a.id}`}>
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-4 p-4 text-right" data-testid={`admin-app-toggle-${a.id}`}>
        {a.discord_avatar ? <img src={a.discord_avatar} alt="" className="h-10 w-10 rounded-full" /> : <div className="h-10 w-10 rounded-full bg-[#5865F2]" />}
        <div className="flex-1">
          <div className="font-semibold">{a.answers["الاسم داخل اللعبة"] || a.discord_username}</div>
          <div className="text-xs text-slate-500" dir="ltr">@{a.discord_username} · {a.created_at.slice(0, 16).replace("T", " ")}</div>
        </div>
        <Tag>{TYPE_LABEL[a.type]}</Tag><Tag tone={STATUS[a.status][1]}>{STATUS[a.status][0]}</Tag>
        <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-sky-400/10 p-5">
          <dl className="grid gap-4 sm:grid-cols-2">
            {Object.entries(a.answers).map(([k, v]) => (
              <div key={k} className={String(v).length > 60 ? "sm:col-span-2" : ""}><dt className="text-xs text-slate-500">{k}</dt><dd className="mt-1 whitespace-pre-line text-sm">{v}</dd></div>
            ))}
            <div><dt className="text-xs text-slate-500">Discord ID</dt><dd className="mt-1 font-mono2 text-sm" dir="ltr">{a.discord_id}</dd></div>
            {a.reviewed_by && <div><dt className="text-xs text-slate-500">راجعه</dt><dd className="mt-1 text-sm">{a.reviewed_by}</dd></div>}
          </dl>
          <textarea className="field mt-5" rows={2} placeholder="ملاحظة (اختياري)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`admin-app-note-${a.id}`} />
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => set("accepted")} className="btn-ice !py-2" data-testid={`admin-app-accept-${a.id}`}><Check size={16} />قبول</button>
            <button onClick={() => set("rejected")} className="btn-ghost !border-rose-400/40 !py-2 text-rose-300" data-testid={`admin-app-reject-${a.id}`}><X size={16} />رفض</button>
            {a.status !== "pending" && <button onClick={() => set("pending")} className="btn-ghost !py-2" data-testid={`admin-app-pending-${a.id}`}>رجّعه للمراجعة</button>}
            <a href={`https://discord.com/users/${a.discord_id}`} target="_blank" rel="noreferrer" className="btn-ghost !py-2">حسابه بالديسكورد</a>
            <button onClick={del} className="ms-auto rounded-full p-2 text-slate-500 hover:text-rose-400" aria-label="حذف" data-testid={`admin-app-delete-${a.id}`}><Trash2 size={17} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminApps() {
  const [status, setStatus] = useState("pending");
  const [type, setType] = useState("");
  const qc = useQueryClient();
  const key = ["admin-apps", status, type];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => api.get("/admin/applications", { params: { status, type } }).then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-apps"] });
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {[["pending", "قيد المراجعة"], ["accepted", "مقبول"], ["rejected", "مرفوض"], ["", "الكل"]].map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} className={`rounded-full border px-4 py-2 text-sm ${status === v ? "border-sky-400 bg-sky-400/15" : "border-sky-400/15 text-slate-400"}`} data-testid={`admin-apps-status-${v || "all"}`}>
            {l}{v && data?.counts && <span className="ms-2 font-mono2 text-xs text-sky-300">{data.counts[v]}</span>}
          </button>
        ))}
        <select value={type} onChange={(e) => setType(e.target.value)} className="field !w-auto !rounded-full !py-2" data-testid="admin-apps-type">
          <option value="">كل الأنواع</option>
          {Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      <div className="mt-6 space-y-3">
        {isLoading ? <div className="h-40 animate-pulse rounded-2xl bg-slate-900/60" /> : !data.items.length
          ? <p className="glass rounded-2xl p-10 text-center text-sm text-slate-400" data-testid="admin-apps-empty">ما فيه تقديمات هنا</p>
          : data.items.map((a) => <AppCard key={a.id} a={a} onChange={refresh} />)}
      </div>
    </div>
  );
}
